// components/roles/EditRoleModal.jsx

import { useState, useEffect } from "react";
import axios from "axios";

const FIELD_LIMITS = {
  name: { min: 4, max: 50 },
  description: { min: 5, max: 255 }
};

function validate(fields) {
  const errors = {};

  const name = fields.name.trim();
  if (!name) {
    errors.name = "Name is required.";
  } else if (name.length < FIELD_LIMITS.name.min) {
    errors.name = `Name must be at least ${FIELD_LIMITS.name.min} characters.`;
  } else if (name.length > FIELD_LIMITS.name.max) {
    errors.name = `Name must not exceed ${FIELD_LIMITS.name.max} characters.`;
  }

  if (fields.description.length > FIELD_LIMITS.description.max) {
    errors.description = `Description must not exceed ${FIELD_LIMITS.description.max} characters.`;
  } else if (fields.description.length < FIELD_LIMITS.description.min) {
    errors.description = `Description must be at least ${FIELD_LIMITS.description.min} characters.`;
  }

  return errors;
}

function CheckboxField({ id, label, checked, onChange, description }) {
  return (
    <label
      htmlFor={id}
      className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-slate-600 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
    >
      <div className="flex items-center h-5 mt-0.5">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 cursor-pointer"
        />
      </div>
      <div className="min-w-0">
        <span className="block text-sm font-medium text-gray-800">{label}</span>
        {description && (
          <span className="block text-xs text-gray-500 mt-0.5">{description}</span>
        )}
      </div>
    </label>
  );
}

export default function EditRoleModal({ role, onClose, onSuccess }) {
  const [fields, setFields] = useState({
    id: 0,
    name: "",
    description: "",
    groupLevel: false,
    active: false,
    system: false,
    dangerous: false,
    locked: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  // Populate form from provided role object
  useEffect (() => {
    if (!role) return;
    
    setFields ({
      id: role.id ?? 0,
      name: role.name ?? "",
      description: role.description ?? "",
      groupLevel: role.group_level ?? false,
      active: role.active ?? false,
      system: role.system ?? false,
      dangerous: role.dangerous ?? false,
      locked: role.locked ?? false,
    });
    setErrors ({});
    setApiError ("");
  }, [role]);

  const handleChange = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFields((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async () => {
    const validationErrors = validate(fields);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting (true);
    setApiError ("");

    try {
      const API_BASE = window.MANTLE_CONFIG.API_URL;
      const token = localStorage.getItem ('jwt');
      const body = {
          id: fields.id,
          name: fields.name.trim(),
          description: fields.description.trim(),
          group_level: fields.groupLevel,
          active: fields.active,
          system: fields.system,
          dangerous: fields.dangerous,
          locked: fields.locked,
        };
    
      const res = await axios.post(`${API_BASE}/users/update-role`, body, {
        headers: {
          'Authorization': 'Bearer ' + token,
          'x-db-name': window.MANTLE_CONFIG.TENANT_DB 
        },
      });
      
      onSuccess(res?.data ?? res); onClose(); 
    } catch {
      setApiError("Network error. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!role) return null;

  const descLength = fields.description.length;
  const descNearLimit = descLength > 220;
  const descOverLimit = descLength > FIELD_LIMITS.description.max;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-lg bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-slate-700 shrink-0">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-slate-100">Edit Role</h2>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
              ID: <span className="font-mono">{role.id}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* Hidden Role ID (informational only — sent in submit payload) */}
          <input type="hidden" name="id" id="id" value={role.id} />

          {/* Name */}
          <div>
            <label htmlFor="role-name" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="role-name"
              type="text"
              value={fields.name}
              onChange={handleChange("name")}
              maxLength={FIELD_LIMITS.name.max}
              placeholder="e.g. Account Manager"
              className={`w-full text-sm px-3 py-2 rounded-lg border bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 transition-colors
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                ${errors.name
                  ? "border-red-400 bg-red-50 focus:ring-red-400"
                  : "border-gray-300 dark:border-slate-600 hover:border-gray-400"
                }`}
            />
            <div className="flex items-center justify-between mt-1">
              {errors.name
                ? <p className="text-xs text-red-600">{errors.name}</p>
                : <p className="text-xs text-gray-400">4–50 characters</p>
              }
              <span className="text-xs text-gray-400 tabular-nums">
                {fields.name.length}/{FIELD_LIMITS.name.max}
              </span>
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="role-description" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
              Description
            </label>
            <textarea
              id="role-description"
              value={fields.description}
              onChange={handleChange("description")}
              rows={3}
              maxLength={FIELD_LIMITS.description.max}
              placeholder="What is this role used for?"
              className={`w-full text-sm px-3 py-2 rounded-lg border bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 resize-none transition-colors
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                ${errors.description
                  ? "border-red-400 bg-red-50 focus:ring-red-400"
                  : "border-gray-300 dark:border-slate-600 hover:border-gray-400"
                }`}
            />
            <div className="flex items-center justify-between mt-1">
              {errors.description
                ? <p className="text-xs text-red-600">{errors.description}</p>
                : <span />
              }
              <span className={`text-xs tabular-nums ${descOverLimit ? "text-red-600 font-medium" : descNearLimit ? "text-amber-600" : "text-gray-400"}`}>
                {descLength}/{FIELD_LIMITS.description.max}
              </span>
            </div>
          </div>

          {/* Flags */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Flags</p>
            <div className="grid grid-cols-1 gap-2">
              <CheckboxField
                id="flag-group-level"
                label="Group Level"
                checked={fields.groupLevel}
                onChange={handleChange("groupLevel")}
                description="Role applies at the group level rather than individual users."
              />
              <CheckboxField
                id="flag-active"
                label="Active"
                checked={fields.active}
                onChange={handleChange("active")}
                description="Inactive roles cannot be assigned to new users."
              />
              <CheckboxField
                id="flag-system"
                label="System Role"
                checked={fields.system}
                onChange={handleChange("system")}
                description="Reserved for internal system use. Exercise caution when modifying."
              />
              <CheckboxField
                id="flag-dangerous"
                label="Dangerous"
                checked={fields.dangerous}
                onChange={handleChange("dangerous")}
                description="Marks this role as high-risk. Users will see a warning when it is assigned."
              />
              <CheckboxField
                id="flag-locked"
                label="Locked"
                checked={fields.locked}
                onChange={handleChange("locked")}
                description="Locked roles cannot be modified or deleted without unlocking first."
              />
            </div>
          </div>

          {/* API error */}
          {apiError && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z" />
              </svg>
              <span>{apiError}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-gray-100 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-200 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 hover:border-gray-400 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting && (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {submitting ? "Saving …" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
